/**
 * MMAGuy ships-note — keep the lead-time promise honest across variant changes.
 *
 * MMAGUY-SHIPSNOTE-VARIANT-SYNC-2026-08-31
 *
 * On a product page `variant-picker.js` morphs ONLY <variant-picker>; every other
 * block updates itself by listening for ThemeEvents.variantUpdate, which carries
 * the freshly fetched document as event.detail.data.html. This block listened to
 * nothing, so picking a SOLD OUT size left the previous size's shipping promise
 * on screen next to a "Sold out" button.
 *
 * The server render is already correct for every variant — this only consumes it.
 * Same idiom as the first-party assets/product-inventory.js.
 */
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class MmaguyShipsNote extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateNote);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateNote);
  }

  /**
   * Replaces this element's children with the freshly rendered ones.
   * An empty replacement is the POINT: a sold-out size renders no note, so the
   * promise must disappear, not merely change.
   * @param {CustomEvent} event - The variant update event.
   */
  updateNote = (event) => {
    const data = event.detail?.data;
    if (!data) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId && this.dataset.productId && data.productId !== this.dataset.productId) {
      // Another product on the same page (quick-add, recommendations) — not ours.
      return;
    }

    const next = data.html?.querySelector('mmaguy-ships-note');
    if (!next) return;

    morph(this, next, { childrenOnly: true });
  };
}

if (!customElements.get('mmaguy-ships-note')) {
  customElements.define('mmaguy-ships-note', MmaguyShipsNote);
}
